"use client";
import { ReactLenis, useLenis } from "lenis/react";
import { useEffect, type ReactNode } from "react";
import { usePathname } from "next/navigation";

const LenisManager = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname();
  const lenis = useLenis();

  useEffect(() => {
    if (!lenis) return;

    // Lands on an element, respecting its CSS scroll-margin-top (e.g. to clear
    // sticky bars). Images above it can still push it down as they load, so
    // keep re-aligning while the layout settles, until the visitor scrolls or
    // a few seconds pass.
    let stopSettling = () => {};
    const scrollToElement = (el: HTMLElement) => {
      stopSettling();
      // Absolute position from the browser's own scroll value: Lenis's
      // element targets use its internal scroll, which can be stale right
      // after Next.js has scrolled the page itself.
      // Lenis also clamps to the page height it last measured, which is the
      // previous page's height right after a navigation, so re-measure first.
      const align = () => {
        lenis.resize();
        const margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
        const top = el.getBoundingClientRect().top + window.scrollY - margin;
        lenis.scrollTo(Math.max(0, top), { immediate: true });
      };
      align();

      const observer = new ResizeObserver(align);
      observer.observe(document.body);
      const userEvents = ["wheel", "touchstart", "keydown", "mousedown"];
      const stop = () => {
        observer.disconnect();
        window.clearTimeout(timer);
        userEvents.forEach((type) => window.removeEventListener(type, stop));
      };
      const timer = window.setTimeout(stop, 6000);
      userEvents.forEach((type) =>
        window.addEventListener(type, stop, { passive: true }),
      );
      stopSettling = stop;
    };

    const findTarget = (id: string): HTMLElement | null => {
      const elById = document.getElementById(id);
      if (elById) return elById;
      if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
        try {
          const el = document.querySelector(`#${CSS.escape(id)}`);
          if (el instanceof HTMLElement) return el;
        } catch {
          return null;
        }
      }
      return null;
    };

    let stopWaiting = () => {};
    const scrollToHash = () => {
      stopWaiting();
      const hash = window.location.hash;

      const scrollToTop = () => lenis.scrollTo(0, { immediate: true });

      if (!hash) {
        scrollToTop();
        return;
      }

      const rawId = hash.startsWith("#") ? hash.slice(1) : hash;

      let id = rawId;
      try {
        id = decodeURIComponent(rawId);
      } catch {
        id = rawId;
      }

      const target = id ? findTarget(id) : null;
      if (target) {
        scrollToElement(target);
        return;
      }

      // After a client-side navigation the target may not have rendered yet
      // (e.g. "Learn More" → /components-products#door-facings). Start at the
      // top and jump once it appears.
      scrollToTop();
      if (!id) return;
      const observer = new MutationObserver(() => {
        const el = findTarget(id);
        if (el) {
          stopWaiting();
          scrollToElement(el);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      const timer = window.setTimeout(() => stopWaiting(), 5000);
      stopWaiting = () => {
        observer.disconnect();
        window.clearTimeout(timer);
      };
    };

    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);

    return () => {
      stopWaiting();
      stopSettling();
      window.removeEventListener("hashchange", scrollToHash);
    };
  }, [pathname, lenis]);

  return children;
};

function SmoothScrolling({ children }: { children: ReactNode }) {
  return (
    <ReactLenis root options={{ lerp: 0.1, duration: 1.5, smoothWheel: true }}>
      <LenisManager>{children}</LenisManager>
    </ReactLenis>
  );
}

export default SmoothScrolling;
